// src/com/user_info/UserInfo.js
import React, { useEffect, useRef, useState } from 'react';
// import { Box } from '@mui/system';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import MenuIcon from '@mui/icons-material/Menu'; // user menu
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // user정보 업로드
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import FlexyTable from '../../common/flexytable/FlexyTable';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, userInfoListRepo, userInfoRepo } from '../../value/AppModel';
import {
    LIST_USER_INFO, GET_USER_INFO, ADD_USER_INFO, EDIT_USER_INFO, REMOVE_USER_INFO,
    SET_HANDLER_USER_INFO, SET_USAGE_USER_INFO
} from './UserInfoGql';
import { UserInfoDictionary } from './UserInfoDict';
import UserInfoView from './UserInfoView';
import UserInfoInput from './UserInfoInput';
import UserInfoToolMenu, { UserInfoToolCmd } from './UserInfoToolMenu';
import UserInfoUploader from './UserInfoUploader';
import InputUtil from '../../value/InputUtil';

/**
 * (USER_INFO) 정보 View Controller
 * 기초정보 > 사용자 관리 > 사용자 조회
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-19 14:49:58 using DbdbTap
 */
export default function UserInfo({
    viewServiceTags, // for switching view
    show = true,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const userInfoListData = useReactiveVar(userInfoListRepo);
    const [searchText, setSearchText] = useState('');
    const [countOfRecordsShowing, setCountOfRecordsShowing] = useState(0);
    const [toolMenuState, setToolMenuState] = useState({open:false, item:null, event:null});
    const [uploadViewState, setUploadViewState] = useState({open:false, csvText:''});
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const checkAuth = useAuthorizationCheck();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [useEucKr, setUseEucKr] = useState(false);
    const [uploadPopAnchorEl, setUploadPopAnchorEl] = useState(null);
    const fileInputRef = useRef(null);
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getUserInfoList, responseList] = useLazyQuery(LIST_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getUserInfoItem, responseItem] = useLazyQuery(GET_USER_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addUserInfo, responseAdd] = useMutation( ADD_USER_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editUserInfo, responseEdit] = useMutation( EDIT_USER_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeUserInfo, responseRemove] = useMutation( REMOVE_USER_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.Remove, message: [error.message]})
	});
    const [setHandlerUserInfo, responseSetHandler] = useMutation( SET_HANDLER_USER_INFO, {
        onCompleted: (data, option) => onCompleteSetHandler(data, option),
        onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.SetHandler, message: [error.message]})
    });
    const [setUsageUserInfo, responseSetUsage] = useMutation( SET_USAGE_USER_INFO, {
        onCompleted: (data, option) => onCompleteSetUsage(data, option),
        onError: (error) => addAlert({title: UserInfoDictionary.errorTitle.SetUsage, message: [error.message]})
    });

    const getUserInfoListNow = () => {
        getUserInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - userInfoListData.when;
        if(diff > 300)
            getUserInfoListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.userInfoList) {
            userInfoListRepo({when:new Date().getTime()/1000, records:data.userInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.userInfoItem) {
            if(isEditingRef.current) setEditorState({open:true, item:data.userInfoItem});
            else setViewerState({open:true, item:data.userInfoItem});
        }
        else addAlert({title:UserInfoDictionary.errorTitle.Get, message:['사용자 정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.userInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getUserInfoListNow();
        }
        else addAlert({title:UserInfoDictionary.errorTitle.Add, message:[data.userInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.userInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getUserInfoListNow();
        }
        else addAlert({title:UserInfoDictionary.errorTitle.Edit, message:[data.userInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.userInfoRemove.ok) getUserInfoListNow();
        else addAlert({title:UserInfoDictionary.errorTitle.Remove, message:[data.userInfoRemove.message]});
    };

    const onCompleteSetHandler = (data, clientOption) => {
        if(data.userInfoSetSecurityHandler.ok) getUserInfoListNow();
        else addAlert({title:UserInfoDictionary.errorTitle.SetHandler, message:[data.userInfoSetSecurityHandler.message]});
    };

    const onCompleteSetUsage = (data, clientOption) => {
        if(data.userInfoSetUsage.ok) getUserInfoListNow();
        else addAlert({title:UserInfoDictionary.errorTitle.SetUsage, message:[data.userInfoSetUsage.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getUserInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{userInfo:item}};
        if(isEdit) editUserInfo(mutateParam);
        else addUserInfo(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getUserInfoItem({variables:{userNid:item.userNid}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "사용자 정보 삭제",
            content: [
                `사용자(${item.userName}) 정보를 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeUserInfo({variables:{userInfo:{userNid:item.userNid}}});
    };

    const onRequestSetHandler = async (item, toSet) => {
        const confirm = await openConfirmDialog({
            title: '정보취급자 ' + (toSet ? '설정' : '해제'),
            content: [
                `사용자(${item.userName})를 정보취급자 ${toSet ? '로 설정' : ' 설정을 해제'}하시겠습니까?`
            ]
        });

        if(confirm) {
            const param = {userNid:item.userNid, handlerYn: toSet ? 'Y' : 'N'};
            setHandlerUserInfo({variables:{userInfo:param}});
        }
    };

    const onRequestSetUsage = async (item, toSet) => {
        const confirm = await openConfirmDialog({
            title: '사용자 사용여부 ' + (toSet ? '설정' : '해제'),
            content: [
                `사용자(${item.userName})의 NewTOP 이용을 ${toSet ? '재개' : '중지'}하도록 변경하시겠습니까?`
            ]
        });

        if(confirm) {
            const param = {userNid:item.userNid, useYn: toSet ? 'Y' : 'N'};
            setUsageUserInfo({variables:{userInfo:param}});
        }
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getUserInfoItem({variables:{userNid:record.userNid}});
    };

    const onCloseView = () => {
        setViewerState(AppObject.EmptyViewerState);
    };

    const onRequestEditFromViewer = (item) => {
        setViewerState(AppObject.EmptyViewerState);
        setEditorState({open: true, item: item});
    };

    const onCsvFileRead = (content, isEucKr) => {
        setUploadPopAnchorEl(null);
        setUploadViewState({open:true, csvText:content});
        setUseEucKr(isEucKr);
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                InputUtil.readTextFileWithAutoEncoding(file, onCsvFileRead);
            }
        }
    };

    const onClickUpload = () => {
        if(fileInputRef.current) {
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onClickToolOnRow = (toolId) => {
        const item = {...toolMenuState.item};
        setToolMenuState({open:false, item:null, event:null});
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(item); break;
            case AppObject.command.EDIT: onRequestEdit(item); break;
            case UserInfoToolCmd.Handler: onRequestSetHandler(item, true); break;
            case UserInfoToolCmd.NoHandler: onRequestSetHandler(item, false); break;
            case UserInfoToolCmd.Use: onRequestSetUsage(item, true); break;
            case UserInfoToolCmd.NoUse: onRequestSetUsage(item, false); break;
            default: return;
        }
    };

    const onClickMenuOnRow = (toolId, row, event) => {
        if(toolId === AppObject.command.VIEW) onDoubleClickRow(row);
        else setToolMenuState({open:true, item:row, event:{clientX:event.clientX, clientY:event.clientY}});
    };

    const canAdd = checkAuth(authTableTree.tree.pr_user_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_user_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_user_info_remove);
    const canSetUsage = checkAuth(authTableTree.tree.pr_user_info_set_usage);
    const canSetHanler = checkAuth(authTableTree.tree.pr_user_info_set_secu_handler);

    const tools = [
        {id:AppObject.command.VIEW, icon:<PageviewIcon/> }
    ];

    if(canEdit || canRemove || canSetUsage || canSetHanler) {
        tools.push(
            {id:'tool', icon:<MenuIcon/>, where:(row)=>sessionInfo.user.classRank < row.classRank},
        );
    }

    const canUpload = checkAuth(authTableTree.tree.pr_user_info_upload);

    const loadingAny
    = responseEdit.loading
    || responseAdd.loading
    || responseRemove.loading
    || responseList.loading
    || responseItem.loading
    || responseSetHandler.loading
    || responseSetUsage.loading;

    /* const renderUploadPop = () => {
        return(
            <Popover
                open={Boolean(uploadPopAnchorEl)}
                anchorEl={uploadPopAnchorEl}
                onClose={()=>setUploadPopAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box style={{padding:10, maxWidth:300}}>
                    <Box style={{
                        marginBottom:10, padding:10, borderBottom:'1px solid #ccc', borderRadius:5,
                        backgroundColor: AppStyle.PrimaryRGB, color:'white'
                    }}>
                        파일 업로드하기
                    </Box>
                    <p style={{fontSize:'0.9rem'}}>
                        사용자 정보 일괄 등록을 위해 CSV 파일을 준비해 주세요.<br/>
                        파일이 CSV 파일로 저장될 때 일반적으로는 UTF-8 인코딩이 사용됩니다.
                        하지만, 오래전에 작성된 파일이나 저장한 프로그램에 따라 EUC-KR 인코딩(완성형)이
                        사용된 경우도 있습니다. 이 경우 아래 체크박스를 선택하세요.<br/>
                        잘 모르시면, 일단 열어본 후 한글이 깨지면 체크 상태를 바꾸고
                        다시 열어보세요.
                    </p>
                    <Box style={{borderTop:'1px solid #ccc'}}>
                        <VzmCheckbox
                            dict={{
                                id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                            }}
                            value={useEucKr}
                            onChange={(id,value,err) => setUseEucKr(value)}
                        />
                    </Box>
                    <Button
                        fullWidth
                        variant={AppStyle.mui.contained}
                        onClick={onClickUpload}
                        startIcon={<CloudUploadIcon/>}
                        disabled={loadingAny}
                    >
                        사용자 정보 일괄 등록
                    </Button>
                </Box>
            </Popover>
        );
    }; */

    const rootStyle = show
        ? {position:'relative'}
        : {position:'relative', display:'none', visibility:'hidden', pointerEvents:'none'};

    return(
        <ContentConainer style={rootStyle}>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countOfRecordsShowing} />
                    {
                        responseEdit.loading ||
                        responseAdd.loading ||
                        responseRemove.loading ||
                        responseList.loading ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                            disabled={loadingAny}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            disabled={loadingAny}
                        >
                            <PersonAddIcon />
                        </IconButton>
                    }
                    <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={onChooseFile}
                    />
                    {
                        fileInputRef.current && canUpload &&
                        <IconButton
                            color={AppStyle.mui.standard}
                            style={{marginLeft:10}}
                            onClick={onClickUpload}
                            title='사용자 정보 일괄등록'
                            disabled={loadingAny}
                        >
                            <CloudUploadIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={UserInfoDictionary.tableProps.tableId}
                tableProps={UserInfoDictionary.tableProps}
                records={userInfoListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickMenuOnRow}
                onRowDoubleClicked={onDoubleClickRow}
                onChangeCountToDisplay={setCountOfRecordsShowing}
            />
            <UserInfoView
                open={viewerState.open}
                item={viewerState.item}
                onRequestEdit={onRequestEditFromViewer}
                onClose={onCloseView}
            />
            <UserInfoInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
            {
                toolMenuState.open &&
                <UserInfoToolMenu
                    item={toolMenuState.item}
                    position={toolMenuState.event}
                    onClose={()=>setToolMenuState({open:false, item:null, event:null})}
                    onClickTool={onClickToolOnRow}
                />
            }
            <UserInfoUploader
                open={uploadViewState.open}
                csvText={uploadViewState.csvText}
                isEucKr={useEucKr}
                onClose={()=>setUploadViewState({open:false, csvText:''})}
            />
        </ContentConainer>
    );
}
